/**
 * Statistics formatting utilities
 */

/**
 * Format win-loss record
 * @param {number} wins
 * @param {number} losses
 * @returns {string} Formatted as "W-L"
 */
export const formatRecord = (wins, losses) => {
  return `${wins}-${losses}`;
};

/**
 * Format percentage
 * @param {number} value - Percentage value (0-100)
 * @returns {string} Formatted with % symbol
 */
export const formatPercentage = (value) => {
  return `${Math.round(value)}%`;
};

/**
 * Format point differential with +/- sign
 * @param {number} differential
 * @returns {string} Formatted differential
 */
export const formatDifferential = (differential) => {
  if (differential > 0) return `+${differential}`;
  return differential.toString();
};

/**
 * Format date for display
 * @param {string|Date} date
 * @param {boolean} includeTime - Include time in format
 * @returns {string} Formatted date
 */
export const formatDate = (date, includeTime = false) => {
  if (!date) return '-';

  const d = new Date(date);
  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    ...(includeTime && {
      hour: '2-digit',
      minute: '2-digit'
    })
  };

  return d.toLocaleDateString('en-US', options);
};

/**
 * Format relative date (e.g., "2 days ago")
 * @param {string|Date} date
 * @returns {string} Relative date string
 */
export const formatRelativeDate = (date) => {
  if (!date) return '-';

  const d = new Date(date);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

/**
 * Format rank with ordinal suffix
 * @param {number} rank
 * @returns {string} Rank with suffix (1st, 2nd, 3rd, etc.)
 */
export const formatRank = (rank) => {
  if (!rank) return '-';

  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = rank % 100;
  return rank + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

/**
 * Format number with commas
 * @param {number} num
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return num.toLocaleString();
};
